import { useState, useEffect, useCallback } from 'react';
import API from './api';
import track from './track';

/**
 * usePush — one implementation of "can this device get notifications, and is it
 * signed up".
 *
 * Two things render this: the small toggle on the leaderboard, and the prompt on
 * the home page. They must agree about what state the device is in, so the logic
 * lives here rather than in each of them.
 *
 * States:
 *   idle          can subscribe, hasn't
 *   subscribed    done
 *   denied        the browser has been told no; only settings can undo it
 *   needs_install iPhone in a Safari tab — capable, but only once installed
 *   unsupported   genuinely can't
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

/**
 * iPhone, in Safari, not installed to the Home Screen.
 *
 * Apple has supported Web Push since iOS 16.4, but ONLY for web apps added to
 * the Home Screen — in an ordinary Safari tab `window.Notification` does not
 * exist at all. Without this check every iPhone visitor falls into the generic
 * "unsupported" branch and is told nothing, when they are two taps from working.
 */
export function isIosSafariTab() {
  const ua = window.navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ claims to be a Mac; the touch points give it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS && !isInstalled();
}

export function isInstalled() {
  return window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
}

export default function usePush() {
  // 'checking', not 'idle'.
  //
  // Working out the real state needs the service worker, so it can only happen
  // after a render. Starting at 'idle' meant the first frame claimed "can
  // subscribe, hasn't" for every device — including the iPhones that can't be
  // asked at all — and anything keyed off that fired before the answer existed.
  // It made "Saw the notification ask" equal "Opened the app" in the funnel,
  // which is a number that looks fine and means nothing.
  const [state, setState] = useState('checking');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      const s = isIosSafariTab() ? 'needs_install' : 'unsupported';
      setState(s);
      // An iPhone that could be notified but isn't installed yet is the single
      // most interesting row in the funnel: it's the gap a native app closes.
      if (s === 'needs_install') track('push_needs_install');
      return;
    }
    if (Notification.permission === 'denied') { setState('denied'); return; }
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      // Resolve BOTH ways. Only setting state when a subscription was found left
      // everyone else sitting on the initial value forever, which is how the
      // initial value came to mean two different things.
      .then(sub => { if (alive) setState(sub ? 'subscribed' : 'idle'); })
      .catch(() => { if (alive) setState('idle'); });
    return () => { alive = false; };
  }, []);

  const subscribe = useCallback(async () => {
    setLoading(true);
    try {
      const keyRes = await API.get('/push/vapid-public-key');
      const publicKey = keyRes.data.publicKey;
      if (!publicKey) { setLoading(false); return false; }

      const reg = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState('denied'); track('push_denied'); setLoading(false); return false;
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      await API.post('/push/subscribe', {
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))))
        }
      });
      setState('subscribed');
      track('push_enabled');
      setLoading(false);
      return true;
    } catch (e) {
      console.warn('[Push] Subscribe failed:', e.message);
      setLoading(false);
      return false;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await API.post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setState('idle');
    } catch (e) { /* leaving it subscribed is the safe failure */ }
    setLoading(false);
  }, []);

  return { state, loading, subscribe, unsubscribe };
}
